import type { PropsWithChildren } from 'react';
import { createUseStyles } from 'react-jss';

interface FlexProps {
  direction: 'vertical' | 'horizontal';
  gap?: keyof typeof gapMap;
  maxWidth?: number | 'unset';
  fullWidth?: boolean;
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems?: 'center' | 'flex-start' | 'flex-end' | 'stretch';
  alignSelf?: 'center' | 'flex-start' | 'flex-end' | 'stretch';
  justifySelf?: 'center' | 'flex-start' | 'flex-end' | 'stretch';
  wrap?: boolean;
  width?: number | 'unset' | 'auto';
  height?: number | 'unset' | 'auto';
}

const gapMap = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

const useStyles = createUseStyles({
  flex: {
    display: 'flex',
    flexWrap: (props: FlexProps) => (props.wrap ? 'wrap' : 'nowrap'),
    flexDirection: (props: FlexProps) => (props.direction === 'vertical' ? 'column' : 'row'),
    gap: (props: FlexProps) => gapMap[props.gap ?? 'md'],
    maxWidth: (props: FlexProps) => props.maxWidth,
    width: (props: FlexProps) =>
      (props.width === 'auto' && props.direction === 'vertical') || props.fullWidth ? '100%' : props.width,
    justifyContent: (props: FlexProps) => props.justifyContent,
    alignItems: (props: FlexProps) => props.alignItems,
    alignSelf: (props: FlexProps) => props.alignSelf,
    justifySelf: (props: FlexProps) => props.justifySelf,
    height: (props: FlexProps) => props.height,
  },
});

export const Flex = ({
  children,
  gap = 'md',
  maxWidth = 'unset',
  direction = 'vertical',
  fullWidth = false,
  justifyContent,
  alignItems,
  alignSelf,
  justifySelf,
  wrap = false,
  width = 'auto',
  height,
}: PropsWithChildren<FlexProps>) => {
  const classes = useStyles({
    gap,
    direction,
    maxWidth,
    fullWidth,
    justifyContent,
    alignItems,
    alignSelf,
    justifySelf,
    wrap,
    width,
    height,
  });

  return <div className={classes.flex}>{children}</div>;
};
